// Waitlist API client (backend docs/WAITLIST.md). Also remembers where a visitor came
// from (UTM tags, a friend's ?ref= code, the first page they landed on) so the sign-up
// can be attributed even if they join a few pages later.
const API_ORIGIN = (process.env.REACT_APP_API_URL || "https://bds-prod.accruewise.in").replace(/\/$/, "");
const BASE = `${API_ORIGIN}/api/v1/waitlist`;

const ATTRIBUTION_KEY = "aw_attribution";
const JOINED_KEY = "aw_waitlist";

const safe = (fn, fallback = null) => {
    try {
        return fn();
    } catch {
        return fallback;
    }
};

/** Call once on page load: keeps the first touch (landing page, UTM tags, ref code). */
export function captureAttribution() {
    safe(() => {
        const params = new URLSearchParams(window.location.search);
        const existing = JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || "null");
        const utm = {};
        ["source", "medium", "campaign", "content", "term"].forEach((k) => {
            const v = params.get(`utm_${k}`);
            if (v) utm[k] = v.slice(0, 100);
        });
        const ref = (params.get("ref") || "").trim().toUpperCase().slice(0, 12);
        const next = existing || {
            landingPath: window.location.pathname,
            referrerUrl: document.referrer ? document.referrer.slice(0, 300) : "",
            utm: {},
            ref: "",
        };
        if (Object.keys(utm).length) next.utm = utm;
        if (ref) next.ref = ref;
        sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(next));
    });
}

export function getAttribution() {
    return safe(() => JSON.parse(sessionStorage.getItem(ATTRIBUTION_KEY) || "null")) || {};
}

/** The sign-up saved on this device, if any: { referralCode, position, platform }. */
export function getSavedJoin() {
    return safe(() => JSON.parse(localStorage.getItem(JOINED_KEY) || "null"));
}

function saveJoin(data) {
    safe(() => localStorage.setItem(JOINED_KEY, JSON.stringify(data)));
}

export function forgetJoin() {
    safe(() => localStorage.removeItem(JOINED_KEY));
}

async function request(path, options = {}) {
    let res;
    try {
        res = await fetch(`${BASE}${path}`, {
            headers: { "Content-Type": "application/json" },
            ...options,
        });
    } catch {
        throw new Error("We couldn't reach Accruewise. Check your connection and try again.");
    }
    const json = await res.json().catch(() => null);
    if (res.status === 429) {
        throw new Error(json?.message || "Too many sign-ups from this network. Please try again later.");
    }
    if (!res.ok || json?.status === false) {
        throw new Error(json?.message || "Something went wrong. Please try again.");
    }
    return json.data;
}

/**
 * Join the waitlist. `form` is { name, phone, email, platform, interests, city, consent,
 * website } (website is the hidden honeypot field).
 */
export async function joinWaitlist(form) {
    const a = getAttribution();
    const data = await request("/join", {
        method: "POST",
        body: JSON.stringify({
            ...form,
            ref: a.ref || undefined,
            utm: a.utm && Object.keys(a.utm).length ? a.utm : undefined,
            landingPath: a.landingPath || window.location.pathname,
            referrerUrl: a.referrerUrl || undefined,
        }),
    });
    saveJoin({ referralCode: data.referralCode, position: data.position, platform: data.platform });
    return data;
}

/** Latest position and referral count for a saved sign-up. */
export function getWaitlistStatus(code) {
    return request(`/status?code=${encodeURIComponent(code)}`);
}

/** { total } people on the list, for social proof. */
export function getWaitlistStats() {
    return request("/stats");
}

/** The link a member shares: the waitlist page with their code. */
export function referralLink(code) {
    const origin = safe(() => window.location.origin, "https://accruewise.in");
    return `${origin}/waitlist?ref=${encodeURIComponent(code)}&utm_source=waitlist&utm_medium=referral`;
}
